import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/env';
import { 
    Download, 
    Share, 
    PlusSquare, 
    Smartphone,
    Info,
    ChevronRight,
    Monitor,
    Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';

const PWAInstall = () => {
    const [platform, setPlatform] = useState('unknown'); // 'ios', 'android', 'desktop'
    const [isSafari, setIsSafari] = useState(false);
    const [appConfig, setAppConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Detect platform
        const ua = window.navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isAndroid = /Android/.test(ua);
        const isSafariBrowser = isIOS && /WebKit/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);

        if (isIOS) setPlatform('ios');
        else if (isAndroid) setPlatform('android');
        else setPlatform('desktop');

        setIsSafari(isSafariBrowser);

        // Fetch public app config
        const fetchConfig = async () => {
            try {
                const res = await fetch(`${API_URL}/global/mobile-app`);
                if (res.ok) {
                    const data = await res.json();
                    setAppConfig(data);
                }
            } catch (err) {
                console.error('Error fetching mobile app config:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, []);

    const renderHeader = () => (
        <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-600 rounded-2xl mx-auto flex items-center justify-center mb-4 shadow-lg shadow-emerald-600/30">
                <span className="text-3xl font-black text-white tracking-tighter">X</span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight m-0">XORA</h1>
            <p className="text-sm font-medium text-slate-500 mt-2">
                Application Mobile Multi-Tenant
            </p>
        </div>
    );

    const renderIOS = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-sm font-black uppercase tracking-wider text-slate-900 mb-4 flex items-center gap-2">
                    <Smartphone size={18} className="text-emerald-600" />
                    Installation sur iOS
                </h2>
                
                {!isSafari && (
                    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <Info size={20} className="text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-800 font-medium m-0 leading-relaxed">
                            Pour installer l'application, veuillez ouvrir cette page dans le navigateur <strong>Safari</strong>.
                        </p>
                    </div>
                )}

                <div className="space-y-5">
                    <div className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-xs shrink-0">1</div>
                        <div className="pt-1.5">
                            <p className="text-sm font-bold text-slate-800 m-0">Appuyez sur le bouton Partager</p>
                            <p className="text-xs text-slate-500 font-medium mt-1">Dans la barre de navigation en bas de l'écran.</p>
                            <div className="mt-3 inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-xl border border-slate-200">
                                <Share size={24} className="text-blue-500" />
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-xs shrink-0">2</div>
                        <div className="pt-1.5">
                            <p className="text-sm font-bold text-slate-800 m-0">Sur l'écran d'accueil</p>
                            <p className="text-xs text-slate-500 font-medium mt-1">Faites défiler vers le bas et sélectionnez "Sur l'écran d'accueil".</p>
                            <div className="mt-3 inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-xl border border-slate-200">
                                <PlusSquare size={24} className="text-slate-700" />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-xs shrink-0">3</div>
                        <div className="pt-1.5">
                            <p className="text-sm font-bold text-slate-800 m-0">Appuyez sur Ajouter</p>
                            <p className="text-xs text-slate-500 font-medium mt-1">En haut à droite de l'écran pour confirmer.</p>
                        </div>
                    </div>
                </div>
            </div>

            {isSafari && (
                <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, type: 'spring' }}
                    className="fixed bottom-8 left-1/2 -translate-x-1/2 w-full max-w-sm px-4 z-50 pointer-events-none"
                >
                    <div className="bg-blue-500 text-white p-4 rounded-2xl shadow-xl flex items-center gap-4 relative">
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-blue-500 rotate-45"></div>
                        <Share size={24} className="shrink-0 animate-bounce" />
                        <p className="text-sm font-bold m-0">Appuyez ici pour installer l'application !</p>
                    </div>
                </motion.div>
            )}
        </div>
    );

    const renderAndroid = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
                <h2 className="text-sm font-black uppercase tracking-wider text-slate-900 mb-6 flex items-center justify-center gap-2">
                    <Smartphone size={18} className="text-emerald-600" />
                    Application Android
                </h2>

                {appConfig?.mobile_app_url ? (
                    <div className="space-y-6">
                        <a 
                            href={appConfig.mobile_app_url}
                            className="inline-flex items-center justify-center w-full gap-2 px-6 py-4 text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md shadow-emerald-600/20 transition active:scale-95"
                        >
                            <Download size={20} />
                            <span>Télécharger l'APK (Android)</span>
                        </a>

                        {appConfig.mobile_app_description && (
                            <div className="text-left p-4 rounded-xl bg-slate-50 border border-slate-100 text-xs">
                                <h3 className="font-black text-slate-700 uppercase tracking-wider mb-2 text-[10px]">Notes de version</h3>
                                <div className="text-slate-600 whitespace-pre-line leading-relaxed font-medium">
                                    {appConfig.mobile_app_description}
                                </div>
                            </div>
                        )}

                        <div className="text-left text-xs text-slate-500 bg-blue-50 p-4 rounded-xl border border-blue-100">
                            <p className="font-bold text-blue-800 mb-1">Comment installer ?</p>
                            <ol className="list-decimal pl-4 space-y-1 text-blue-700/80">
                                <li>Téléchargez le fichier APK via le bouton ci-dessus.</li>
                                <li>Ouvrez le fichier téléchargé.</li>
                                <li>Autorisez l'installation d'applications de sources inconnues si demandé.</li>
                            </ol>
                        </div>
                    </div>
                ) : (
                    <div className="p-6 bg-slate-50 rounded-xl border border-slate-100">
                        <p className="text-sm font-bold text-slate-700 m-0">L'application Android n'est pas disponible pour le moment.</p>
                        <p className="text-xs text-slate-500 mt-2 font-medium">Veuillez réessayer plus tard.</p>
                    </div>
                )}
            </div>
        </div>
    );

    const renderDesktop = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Monitor size={28} className="text-slate-400" />
                </div>
                <h2 className="text-sm font-black uppercase tracking-wider text-slate-900 mb-3">
                    Version Mobile Uniquement
                </h2>
                <p className="text-sm text-slate-500 font-medium leading-relaxed max-w-sm mx-auto">
                    Veuillez ouvrir cette page sur votre smartphone (iOS ou Android) pour installer l'application Xora.
                </p>
                <div className="mt-6 pt-6 border-t border-slate-100">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Scannez pour ouvrir</p>
                    <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.href)}`}
                        alt="QR Code URL"
                        className="w-[150px] h-[150px] mx-auto rounded-xl border border-slate-200 p-2 bg-white shadow-sm"
                    />
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
                <Loader2 size={32} className="text-emerald-600 animate-spin mb-4" />
                <p className="text-sm font-bold text-slate-500">Chargement...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col p-6 font-sans">
            <div className="w-full max-w-md mx-auto pt-8 pb-20">
                {renderHeader()}
                
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {platform === 'ios' && renderIOS()}
                    {platform === 'android' && renderAndroid()}
                    {platform === 'desktop' && renderDesktop()}
                </motion.div>
            </div>
        </div>
    );
};

export default PWAInstall;
